import { appManager, AppState } from '../manager';
import {
  emptySyntheticBondFamilyMetadata,
  emptySyntheticBondSourceMap,
  initialSyntheticBondGenerationDraft,
  SyntheticBondBatchPreview,
  SyntheticBondBatchStatus,
  SyntheticBondCatalogueResponse,
  SyntheticBondCandidatePricePreview,
  SyntheticBondFamilyMetadata,
  SyntheticBondGenerationDraft,
  SyntheticBondGenerationOptions,
  SyntheticBondPreviewCandidateV2Event,
  SyntheticBondPreviewJobV2,
  SyntheticBondReferenceCurvesResponse,
  SyntheticBondReferenceCurvePreset,
  SyntheticBondReferenceCurvePresetResponse,
  SyntheticBondSeries,
  SyntheticBondSeriesResponse,
  SyntheticBondSourceAssetSearchResponse,
  SyntheticBondCurveTenorMonths,
  syntheticBondDraftKey,
  syntheticBondFamilyKey,
  syntheticBondPreviewId,
  syntheticBondReferenceCurvePresetKey,
  syntheticBondSourceSearchKey,
} from './types';
import { normalizeSyntheticBondApiError } from './apiErrors';

const apiErrorText = (error: unknown, fallback?: string) =>
  normalizeSyntheticBondApiError(error, fallback).message;

const apiErrorCode = (error: unknown) =>
  normalizeSyntheticBondApiError(error).code;

const invalidateReviewedPreview = (state: AppState) => {
  state.synthetic_bonds.preview = undefined;
  state.synthetic_bonds.previewing = false;
  state.synthetic_bonds.previewError = undefined;
  state.synthetic_bonds.previewRequestId = undefined;
  state.synthetic_bonds.previewRequestRevision = undefined;
  state.synthetic_bonds.createError = undefined;
  state.synthetic_bonds.candidatePreviewByKey = {};
  state.synthetic_bonds.activePreviewV2JobId = undefined;
  state.synthetic_bonds.previewV2 = undefined;
  state.synthetic_bonds.previewingV2 = false;
  state.synthetic_bonds.previewV2Error = undefined;
  state.synthetic_bonds.previewV2RequestId = undefined;
  state.synthetic_bonds.previewV2RequestRevision = undefined;
  state.synthetic_bonds.previewV2StatusFetching = false;
  state.synthetic_bonds.candidatePreviewsV2ByJobId = {};
  state.synthetic_bonds.candidatePreviewChunksV2ByJobId = {};
  state.synthetic_bonds.draftRevision += 1;
};

export const updateSyntheticBondDraft = appManager.createLocalEvent(
  'UPDATE_SYNTHETIC_BOND_DRAFT',
  (state: AppState, patch: Partial<SyntheticBondGenerationDraft>) => {
    state.synthetic_bonds.draftSpec = {
      ...state.synthetic_bonds.draftSpec,
      ...patch,
    };
    invalidateReviewedPreview(state);
  },
);

export const selectSyntheticBondSeries = appManager.createLocalEvent(
  'SELECT_SYNTHETIC_BOND_SERIES',
  (state: AppState, payload: { series?: SyntheticBondSeries }) => {
    const series = payload.series;
    if (!series) {
      state.synthetic_bonds.draftSpec = {
        ...initialSyntheticBondGenerationDraft(),
        first_issue_date: state.synthetic_bonds.draftSpec.first_issue_date,
        last_issue_date: state.synthetic_bonds.draftSpec.last_issue_date,
      };
      state.synthetic_bonds.selectedReferenceCurvePresetId = undefined;
    } else {
      state.synthetic_bonds.draftSpec = {
        ...state.synthetic_bonds.draftSpec,
        series_id: series.id,
        currency_id: series.currency.id,
        family_label: series.family_label,
        source_asset_ids_by_tenor_months: {
          ...series.source_asset_ids_by_tenor_months,
        },
        family_metadata: {
          ...emptySyntheticBondFamilyMetadata(),
          ...series.family_metadata_defaults,
          country_code: series.country_code,
          issuer_type: 'Sovereign',
        },
        initial_tenors: [series.initial_tenor_years],
      };
      state.synthetic_bonds.selectedReferenceCurvePresetId =
        series.reference_curve_preset_id;
    }
    state.synthetic_bonds.seriesSaveError = undefined;
    invalidateReviewedPreview(state);
  },
);

export const changeSyntheticBondCurrency = appManager.createLocalEvent(
  'CHANGE_SYNTHETIC_BOND_CURRENCY',
  (state: AppState, payload: { currency_id: number | null }) => {
    state.synthetic_bonds.draftSpec = {
      ...state.synthetic_bonds.draftSpec,
      series_id: null,
      currency_id: payload.currency_id,
      source_asset_ids_by_tenor_months: emptySyntheticBondSourceMap(),
      family_metadata: emptySyntheticBondFamilyMetadata(),
    };
    state.synthetic_bonds.selectedReferenceCurvePresetId = undefined;
    state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
    invalidateReviewedPreview(state);
  },
);

export const applySyntheticBondReferenceCurvePreset =
  appManager.createLocalEvent(
    'APPLY_SYNTHETIC_BOND_REFERENCE_CURVE_PRESET',
    (
      state: AppState,
      payload: {
        preset_id: string;
        source_asset_ids_by_tenor_months: SyntheticBondGenerationDraft['source_asset_ids_by_tenor_months'];
      },
    ) => {
      state.synthetic_bonds.selectedReferenceCurvePresetId = payload.preset_id;
      state.synthetic_bonds.draftSpec.source_asset_ids_by_tenor_months = {
        ...payload.source_asset_ids_by_tenor_months,
      };
      state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
      invalidateReviewedPreview(state);
    },
  );

export const clearSyntheticBondReferenceCurvePreset =
  appManager.createLocalEvent(
    'CLEAR_SYNTHETIC_BOND_REFERENCE_CURVE_PRESET',
    (state: AppState) => {
      state.synthetic_bonds.selectedReferenceCurvePresetId = undefined;
      state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
    },
  );

export const updateSyntheticBondFamilyMetadata = appManager.createLocalEvent(
  'UPDATE_SYNTHETIC_BOND_FAMILY_METADATA',
  (
    state: AppState,
    payload: {
      field: keyof SyntheticBondFamilyMetadata;
      value: string | number | null;
    },
  ) => {
    state.synthetic_bonds.draftSpec.family_metadata = {
      ...state.synthetic_bonds.draftSpec.family_metadata,
      [payload.field]: payload.value,
    };
    invalidateReviewedPreview(state);
  },
);

export const selectSyntheticBondSourceAsset = appManager.createLocalEvent(
  'SELECT_SYNTHETIC_BOND_SOURCE_ASSET',
  (
    state: AppState,
    payload: {
      tenor_months: SyntheticBondCurveTenorMonths;
      asset_id: number | null;
    },
  ) => {
    state.synthetic_bonds.draftSpec.source_asset_ids_by_tenor_months = {
      ...state.synthetic_bonds.draftSpec.source_asset_ids_by_tenor_months,
      [String(payload.tenor_months)]: payload.asset_id,
    };
    invalidateReviewedPreview(state);
  },
);

export const clearSyntheticBondPreview = appManager.createLocalEvent(
  'CLEAR_SYNTHETIC_BOND_PREVIEW',
  (state: AppState) => {
    invalidateReviewedPreview(state);
  },
);

export const resetSyntheticBondGenerationDraft = appManager.createLocalEvent(
  'RESET_SYNTHETIC_BOND_GENERATION_DRAFT',
  (state: AppState) => {
    state.synthetic_bonds.draftSpec = initialSyntheticBondGenerationDraft();
    state.synthetic_bonds.selectedReferenceCurvePresetId = undefined;
    state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
    invalidateReviewedPreview(state);
  },
);

export const setActiveSyntheticBondBatch = appManager.createLocalEvent(
  'SET_ACTIVE_SYNTHETIC_BOND_BATCH',
  (state: AppState, payload: { batch_id?: string }) => {
    state.synthetic_bonds.activeBatchId = payload.batch_id;
    state.synthetic_bonds.activeBatch = undefined;
    state.synthetic_bonds.statusFetching = false;
    state.synthetic_bonds.statusError = undefined;
    state.synthetic_bonds.statusRequestId = undefined;
    state.synthetic_bonds.retrying = false;
    state.synthetic_bonds.retryError = undefined;
    state.synthetic_bonds.retryRequestId = undefined;
  },
);

export const fetchSyntheticBondGenerationOptions = appManager.createApi<
  Record<string, never>,
  SyntheticBondGenerationOptions,
  AppState
>('FETCH_SYNTHETIC_BOND_GENERATION_OPTIONS', {
  path: '/synthetic_bond_generation/options',
  method: 'GET',
  startReducer(state) {
    state.synthetic_bonds.optionsFetching = true;
    state.synthetic_bonds.optionsError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.optionsFetching = false;
    state.synthetic_bonds.optionsError = apiErrorText(error);
  },
  successReducer(state, result) {
    state.synthetic_bonds.options = result;
    state.synthetic_bonds.optionsFetching = false;
    state.synthetic_bonds.optionsError = undefined;
  },
});

export const fetchSyntheticBondSeries = appManager.createApi<
  Record<string, never>,
  SyntheticBondSeriesResponse,
  AppState
>('FETCH_SYNTHETIC_BOND_SERIES', {
  path: '/synthetic_bond_generation/series',
  method: 'GET',
  startReducer(state) {
    state.synthetic_bonds.seriesFetching = true;
    state.synthetic_bonds.seriesError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.seriesFetching = false;
    state.synthetic_bonds.seriesError = apiErrorText(error);
  },
  successReducer(state, result) {
    state.synthetic_bonds.series = result.results;
    state.synthetic_bonds.seriesFetching = false;
    state.synthetic_bonds.seriesError = undefined;
  },
});

export const fetchSyntheticBondCatalogue = appManager.createApi<
  Record<string, never>,
  SyntheticBondCatalogueResponse,
  AppState
>('FETCH_SYNTHETIC_BOND_CATALOGUE', {
  path: '/synthetic_bond_generation/catalogue',
  method: 'GET',
  startReducer(state) {
    state.synthetic_bonds.catalogueFetching = true;
    state.synthetic_bonds.catalogueError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.catalogueFetching = false;
    state.synthetic_bonds.catalogueError = apiErrorText(error);
  },
  successReducer(state, result) {
    state.synthetic_bonds.catalogue = result.results;
    state.synthetic_bonds.catalogueFetching = false;
    state.synthetic_bonds.catalogueError = undefined;
  },
});

export type SyntheticBondSeriesWritePayload = {
  country_code: string;
  initial_tenor_years: number;
  currency_id: number;
  reference_curve_preset_id: string;
  family_label: string;
  family_metadata_defaults: Record<string, string | number | null>;
  is_active: boolean;
};

export const createSyntheticBondSeries = appManager.createApi<
  SyntheticBondSeriesWritePayload,
  SyntheticBondSeries,
  AppState
>('CREATE_SYNTHETIC_BOND_SERIES', {
  path: '/synthetic_bond_generation/series',
  method: 'POST',
  startReducer(state) {
    state.synthetic_bonds.seriesSaving = true;
    state.synthetic_bonds.seriesSaveError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.seriesSaving = false;
    state.synthetic_bonds.seriesSaveError = apiErrorText(error);
  },
  successReducer(state, result) {
    const existing = state.synthetic_bonds.series || [];
    state.synthetic_bonds.series = [...existing, result].sort((left, right) =>
      left.code.localeCompare(right.code),
    );
    state.synthetic_bonds.seriesSaving = false;
    state.synthetic_bonds.seriesSaveError = undefined;
    state.synthetic_bonds.draftSpec = {
      ...state.synthetic_bonds.draftSpec,
      series_id: result.id,
      currency_id: result.currency.id,
      family_label: result.family_label,
      source_asset_ids_by_tenor_months: {
        ...result.source_asset_ids_by_tenor_months,
      },
      family_metadata: {
        ...emptySyntheticBondFamilyMetadata(),
        ...result.family_metadata_defaults,
        country_code: result.country_code,
        issuer_type: 'Sovereign',
      },
      initial_tenors: [result.initial_tenor_years],
    };
    state.synthetic_bonds.selectedReferenceCurvePresetId =
      result.reference_curve_preset_id;
    invalidateReviewedPreview(state);
  },
});

type SourceSearchPayload = {
  currency_id: number;
};

export const searchSyntheticBondSourceAssets = appManager.createApi<
  SourceSearchPayload,
  SyntheticBondSourceAssetSearchResponse,
  AppState
>('SEARCH_SYNTHETIC_BOND_SOURCE_ASSETS', {
  path: '/synthetic_bond_generation/source-assets',
  method: 'GET',
  startReducer(state, payload) {
    const key = syntheticBondSourceSearchKey(payload.currency_id);
    const previous = state.synthetic_bonds.sourceAssetSearchByKey[key];
    state.synthetic_bonds.sourceAssetSearchByKey[key] = {
      ...previous,
      fetched: previous?.fetched || false,
      fetching: true,
      error: undefined,
    };
  },
  failReducer(state, error, payload) {
    const key = syntheticBondSourceSearchKey(payload.currency_id);
    state.synthetic_bonds.sourceAssetSearchByKey[key] = {
      ...state.synthetic_bonds.sourceAssetSearchByKey[key],
      fetched: false,
      fetching: false,
      error: apiErrorText(error),
    };
  },
  successReducer(state, result, payload) {
    const key = syntheticBondSourceSearchKey(payload.currency_id);
    state.synthetic_bonds.sourceAssetSearchByKey[key] = {
      data: result,
      fetched: true,
      fetching: false,
      error: undefined,
    };
  },
});

type ReferenceCurvePresetListPayload = {
  currency_id: number;
};

export const fetchSyntheticBondReferenceCurvePresets = appManager.createApi<
  ReferenceCurvePresetListPayload,
  SyntheticBondReferenceCurvePresetResponse,
  AppState
>('FETCH_SYNTHETIC_BOND_REFERENCE_CURVE_PRESETS', {
  path: '/synthetic_bond_generation/source-families',
  method: 'GET',
  startReducer(state, payload) {
    const key = syntheticBondReferenceCurvePresetKey(payload.currency_id);
    const previous =
      state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key];
    state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key] = {
      ...previous,
      fetched: previous?.fetched || false,
      fetching: true,
      error: undefined,
    };
  },
  failReducer(state, error, payload) {
    const key = syntheticBondReferenceCurvePresetKey(payload.currency_id);
    state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key] = {
      ...state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key],
      fetched: false,
      fetching: false,
      error: apiErrorText(error),
    };
  },
  successReducer(state, result, payload) {
    const key = syntheticBondReferenceCurvePresetKey(payload.currency_id);
    state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key] = {
      data: result,
      fetched: true,
      fetching: false,
      error: undefined,
    };
  },
});

export type SyntheticBondReferenceCurvePresetWritePayload = {
  id?: string;
  currency_id: number;
  code: string;
  label: string;
  source_asset_ids_by_tenor_months: SyntheticBondGenerationDraft['source_asset_ids_by_tenor_months'];
  is_active: boolean;
};

const storeReferenceCurvePreset = (
  state: AppState,
  result: SyntheticBondReferenceCurvePreset,
) => {
  const key = syntheticBondReferenceCurvePresetKey(result.currency.id);
  const previous =
    state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key];
  const results = [...(previous?.data?.results || [])];
  const index = results.findIndex(preset => preset.id === result.id);
  if (index >= 0) results[index] = result;
  else results.push(result);
  results.sort((left, right) => left.code.localeCompare(right.code));
  state.synthetic_bonds.referenceCurvePresetsByCurrencyKey[key] = {
    data: { results },
    fetched: true,
    fetching: false,
    error: undefined,
  };
  if (state.synthetic_bonds.draftSpec.currency_id === result.currency.id) {
    state.synthetic_bonds.selectedReferenceCurvePresetId = result.id;
  }
  state.synthetic_bonds.referenceCurvePresetSaving = false;
  state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
};

export const createSyntheticBondReferenceCurvePreset = appManager.createApi<
  SyntheticBondReferenceCurvePresetWritePayload,
  SyntheticBondReferenceCurvePreset,
  AppState
>('CREATE_SYNTHETIC_BOND_REFERENCE_CURVE_PRESET', {
  path: '/synthetic_bond_generation/source-families',
  method: 'POST',
  startReducer(state) {
    state.synthetic_bonds.referenceCurvePresetSaving = true;
    state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.referenceCurvePresetSaving = false;
    state.synthetic_bonds.referenceCurvePresetSaveError = apiErrorText(error);
  },
  successReducer(state, result) {
    storeReferenceCurvePreset(state, result);
  },
});

export const updateSyntheticBondReferenceCurvePreset = appManager.createApi<
  SyntheticBondReferenceCurvePresetWritePayload & { id: string },
  SyntheticBondReferenceCurvePreset,
  AppState
>('UPDATE_SYNTHETIC_BOND_REFERENCE_CURVE_PRESET', {
  path: '/synthetic_bond_generation/source-families/:id',
  method: 'PUT',
  startReducer(state) {
    state.synthetic_bonds.referenceCurvePresetSaving = true;
    state.synthetic_bonds.referenceCurvePresetSaveError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.referenceCurvePresetSaving = false;
    state.synthetic_bonds.referenceCurvePresetSaveError = apiErrorText(error);
  },
  successReducer(state, result) {
    storeReferenceCurvePreset(state, result);
  },
});

type ReferenceCurvesPayload = Pick<
  SyntheticBondGenerationDraft,
  'currency_id' | 'source_asset_ids_by_tenor_months'
>;

export const fetchSyntheticBondReferenceCurves = appManager.createApi<
  ReferenceCurvesPayload,
  SyntheticBondReferenceCurvesResponse,
  AppState
>('FETCH_SYNTHETIC_BOND_REFERENCE_CURVES', {
  path: '/synthetic_bond_generation/reference-curves',
  method: 'POST',
  startReducer(state, payload) {
    const key = syntheticBondFamilyKey(
      payload.currency_id,
      payload.source_asset_ids_by_tenor_months,
    );
    const previous = state.synthetic_bonds.referenceCurvesByFamilyKey[key];
    state.synthetic_bonds.referenceCurvesByFamilyKey[key] = {
      ...previous,
      fetched: previous?.fetched || false,
      fetching: true,
      error: undefined,
    };
  },
  failReducer(state, error, payload) {
    const key = syntheticBondFamilyKey(
      payload.currency_id,
      payload.source_asset_ids_by_tenor_months,
    );
    state.synthetic_bonds.referenceCurvesByFamilyKey[key] = {
      ...state.synthetic_bonds.referenceCurvesByFamilyKey[key],
      fetched: false,
      fetching: false,
      error: apiErrorText(error),
    };
  },
  successReducer(state, result, payload) {
    const key = syntheticBondFamilyKey(
      payload.currency_id,
      payload.source_asset_ids_by_tenor_months,
    );
    state.synthetic_bonds.referenceCurvesByFamilyKey[key] = {
      data: result,
      fetched: true,
      fetching: false,
      error: undefined,
    };
  },
});

export type SyntheticBondPreviewRequest = SyntheticBondGenerationDraft & {
  _client_request_id: string;
  _client_draft_revision: number;
};

export const previewSyntheticBondBatch = appManager.createApi<
  SyntheticBondPreviewRequest,
  SyntheticBondBatchPreview,
  AppState
>('PREVIEW_SYNTHETIC_BOND_BATCH', {
  path: '/synthetic_bond_generation/preview',
  method: 'POST',
  startReducer(state, payload) {
    state.synthetic_bonds.preview = undefined;
    state.synthetic_bonds.previewing = true;
    state.synthetic_bonds.previewError = undefined;
    state.synthetic_bonds.createError = undefined;
    state.synthetic_bonds.candidatePreviewByKey = {};
    state.synthetic_bonds.previewRequestId = payload._client_request_id;
    state.synthetic_bonds.previewRequestRevision =
      payload._client_draft_revision;
  },
  failReducer(state, error, payload) {
    if (
      state.synthetic_bonds.previewRequestId !== payload._client_request_id ||
      state.synthetic_bonds.previewRequestRevision !==
        payload._client_draft_revision ||
      state.synthetic_bonds.draftRevision !== payload._client_draft_revision ||
      syntheticBondDraftKey(state.synthetic_bonds.draftSpec) !==
        syntheticBondDraftKey(payload)
    ) {
      return;
    }
    state.synthetic_bonds.previewing = false;
    state.synthetic_bonds.previewRequestId = undefined;
    state.synthetic_bonds.previewRequestRevision = undefined;
    state.synthetic_bonds.previewError = apiErrorText(error, 'Preview failed.');
    if (apiErrorCode(error) === 'PREVIEW_STALE') {
      state.synthetic_bonds.preview = undefined;
      state.synthetic_bonds.candidatePreviewByKey = {};
    }
  },
  successReducer(state, result, payload) {
    if (
      state.synthetic_bonds.previewRequestId !== payload._client_request_id ||
      state.synthetic_bonds.previewRequestRevision !==
        payload._client_draft_revision ||
      state.synthetic_bonds.draftRevision !== payload._client_draft_revision ||
      syntheticBondDraftKey(state.synthetic_bonds.draftSpec) !==
        syntheticBondDraftKey(payload)
    ) {
      return;
    }
    state.synthetic_bonds.preview = result;
    state.synthetic_bonds.previewing = false;
    state.synthetic_bonds.previewRequestId = undefined;
    state.synthetic_bonds.previewRequestRevision = undefined;
    state.synthetic_bonds.previewError = undefined;
    state.synthetic_bonds.candidatePreviewByKey = {};
  },
});

export type SyntheticBondPreviewV2Request = SyntheticBondGenerationDraft & {
  _client_request_id: string;
  _client_draft_revision: number;
};

const previewJobErrorText = (job: SyntheticBondPreviewJobV2) =>
  apiErrorText(job.error, 'Preview failed in the background worker.');

const isCurrentPreviewV2Job = (
  state: AppState,
  job: SyntheticBondPreviewJobV2,
) =>
  state.synthetic_bonds.activePreviewV2JobId === job.preview_job_id ||
  (Boolean(state.synthetic_bonds.previewV2RequestId) &&
    state.synthetic_bonds.previewV2RequestId === job.client_request_id &&
    state.synthetic_bonds.previewV2RequestRevision ===
      job.client_draft_revision &&
    state.synthetic_bonds.draftRevision === job.client_draft_revision);

const storePreviewV2Job = (state: AppState, job: SyntheticBondPreviewJobV2) => {
  const previous = state.synthetic_bonds.previewV2JobsById[job.preview_job_id];
  const statusRank = {
    PENDING: 0,
    RUNNING: 1,
    SUCCEEDED: 2,
    FAILED: 2,
  } as const;
  const incomingIsOlder = Boolean(
    previous && statusRank[previous.status] > statusRank[job.status],
  );
  const stored = incomingIsOlder
    ? { ...job, ...previous }
    : { ...previous, ...job };
  state.synthetic_bonds.previewV2JobsById[job.preview_job_id] = stored;
  const isCurrent = isCurrentPreviewV2Job(state, stored);
  if (job.candidate_previews && isCurrent) {
    state.synthetic_bonds.candidatePreviewsV2ByJobId[job.preview_job_id] = {
      ...(state.synthetic_bonds.candidatePreviewsV2ByJobId[
        job.preview_job_id
      ] || {}),
      ...job.candidate_previews,
    };
    state.synthetic_bonds.candidatePreviewChunksV2ByJobId[job.preview_job_id] =
      {};
  }
  if (!isCurrent) return;
  state.synthetic_bonds.activePreviewV2JobId = job.preview_job_id;
  if (stored.preview) state.synthetic_bonds.previewV2 = stored.preview;
  state.synthetic_bonds.previewingV2 = !['SUCCEEDED', 'FAILED'].includes(
    stored.status,
  );
  state.synthetic_bonds.previewV2Error =
    stored.status === 'FAILED' ? previewJobErrorText(stored) : undefined;
  if (['SUCCEEDED', 'FAILED'].includes(stored.status)) {
    state.synthetic_bonds.previewV2RequestId = undefined;
    state.synthetic_bonds.previewV2RequestRevision = undefined;
  }
};

export const previewSyntheticBondBatchV2 = appManager.createApi<
  SyntheticBondPreviewV2Request,
  SyntheticBondPreviewJobV2,
  AppState
>('PREVIEW_SYNTHETIC_BOND_BATCH_V2', {
  path: '/synthetic_bond_generation/preview-v2',
  method: 'POST',
  startReducer(state, payload) {
    state.synthetic_bonds.activePreviewV2JobId = undefined;
    state.synthetic_bonds.previewV2 = undefined;
    state.synthetic_bonds.previewingV2 = true;
    state.synthetic_bonds.previewV2Error = undefined;
    state.synthetic_bonds.previewV2StatusFetching = false;
    state.synthetic_bonds.createError = undefined;
    state.synthetic_bonds.candidatePreviewsV2ByJobId = {};
    state.synthetic_bonds.candidatePreviewChunksV2ByJobId = {};
    state.synthetic_bonds.previewV2RequestId = payload._client_request_id;
    state.synthetic_bonds.previewV2RequestRevision =
      payload._client_draft_revision;
  },
  failReducer(state, error, payload) {
    if (
      state.synthetic_bonds.previewV2RequestId !== payload._client_request_id ||
      state.synthetic_bonds.previewV2RequestRevision !==
        payload._client_draft_revision ||
      state.synthetic_bonds.draftRevision !== payload._client_draft_revision ||
      syntheticBondDraftKey(state.synthetic_bonds.draftSpec) !==
        syntheticBondDraftKey(payload)
    ) {
      return;
    }
    state.synthetic_bonds.previewingV2 = false;
    state.synthetic_bonds.previewV2RequestId = undefined;
    state.synthetic_bonds.previewV2RequestRevision = undefined;
    state.synthetic_bonds.previewV2Error = apiErrorText(
      error,
      'Preview could not be queued.',
    );
  },
  successReducer(state, result, payload) {
    if (
      state.synthetic_bonds.previewV2RequestId !== payload._client_request_id ||
      state.synthetic_bonds.previewV2RequestRevision !==
        payload._client_draft_revision ||
      state.synthetic_bonds.draftRevision !== payload._client_draft_revision ||
      syntheticBondDraftKey(state.synthetic_bonds.draftSpec) !==
        syntheticBondDraftKey(payload)
    ) {
      return;
    }
    const existing =
      state.synthetic_bonds.previewV2JobsById[result.preview_job_id];
    storePreviewV2Job(state, existing ? { ...result, ...existing } : result);
  },
});

export const fetchSyntheticBondPreviewJobV2 = appManager.createApi<
  { preview_job_id: string },
  SyntheticBondPreviewJobV2,
  AppState
>('FETCH_SYNTHETIC_BOND_PREVIEW_JOB_V2', {
  path: '/synthetic_bond_generation/preview-v2/jobs/:preview_job_id',
  method: 'GET',
  startReducer(state) {
    state.synthetic_bonds.previewV2StatusFetching = true;
  },
  failReducer(state, error, payload) {
    state.synthetic_bonds.previewV2StatusFetching = false;
    const activeJob =
      state.synthetic_bonds.previewV2JobsById[payload.preview_job_id];
    if (
      state.synthetic_bonds.activePreviewV2JobId === payload.preview_job_id &&
      (!activeJob || !['SUCCEEDED', 'FAILED'].includes(activeJob.status))
    ) {
      state.synthetic_bonds.previewV2Error = apiErrorText(
        error,
        'Could not refresh the preview job.',
      );
    }
  },
  successReducer(state, result) {
    state.synthetic_bonds.previewV2StatusFetching = false;
    storePreviewV2Job(state, result);
  },
});

appManager.createSocketListener<SyntheticBondPreviewCandidateV2Event, AppState>(
  'synthetic_bond_preview_v2_candidate',
  (state, result) => {
    const isActiveJob =
      state.synthetic_bonds.activePreviewV2JobId === result.preview_job_id;
    const isPendingCurrentRequest = Boolean(
      state.synthetic_bonds.previewV2RequestId &&
        state.synthetic_bonds.previewV2RequestId === result.client_request_id &&
        state.synthetic_bonds.previewV2RequestRevision ===
          result.client_draft_revision &&
        state.synthetic_bonds.draftRevision === result.client_draft_revision,
    );
    if (!isActiveJob && !isPendingCurrentRequest) return;

    const chunkIndex = Number(result.chunk_index ?? 0);
    const chunkTotal = Number(result.chunk_total ?? 1);
    if (
      !Number.isInteger(chunkIndex) ||
      !Number.isInteger(chunkTotal) ||
      chunkIndex < 0 ||
      chunkTotal < 1 ||
      chunkIndex >= chunkTotal
    ) {
      return;
    }

    const previews =
      state.synthetic_bonds.candidatePreviewsV2ByJobId[result.preview_job_id] ||
      {};
    if (chunkTotal === 1) {
      state.synthetic_bonds.candidatePreviewsV2ByJobId[result.preview_job_id] =
        {
          ...previews,
          [result.item_id]: result.candidate_preview,
        };
      return;
    }

    const jobBuffers =
      state.synthetic_bonds.candidatePreviewChunksV2ByJobId[
        result.preview_job_id
      ] || {};
    const previousBuffer = jobBuffers[result.item_id];
    const candidatePreview = {
      ...previousBuffer?.candidate_preview,
      ...result.candidate_preview,
      issued_bond: {
        ...previousBuffer?.candidate_preview.issued_bond,
        ...result.candidate_preview.issued_bond,
        price_series: [],
      },
    };
    const chunks = {
      ...previousBuffer?.chunks,
      [String(chunkIndex)]:
        result.candidate_preview.issued_bond?.price_series ||
        result.candidate_preview.price_series ||
        result.candidate_preview.points ||
        [],
    };
    const buffer = {
      candidate_preview: candidatePreview,
      chunks,
      chunk_total: chunkTotal,
      price_point_total: result.price_point_total,
    };
    state.synthetic_bonds.candidatePreviewChunksV2ByJobId[
      result.preview_job_id
    ] = {
      ...jobBuffers,
      [result.item_id]: buffer,
    };

    const complete = Array.from({ length: chunkTotal }).every((_value, index) =>
      Boolean(chunks[String(index)]),
    );
    if (!complete) return;
    const priceSeries = Array.from({ length: chunkTotal }).flatMap(
      (_value, index) => chunks[String(index)],
    );
    if (
      typeof result.price_point_total === 'number' &&
      priceSeries.length !== result.price_point_total
    ) {
      return;
    }
    state.synthetic_bonds.candidatePreviewsV2ByJobId[result.preview_job_id] = {
      ...previews,
      [result.item_id]: {
        ...candidatePreview,
        issued_bond: {
          ...candidatePreview.issued_bond,
          price_series: priceSeries,
        },
      },
    };
    delete state.synthetic_bonds.candidatePreviewChunksV2ByJobId[
      result.preview_job_id
    ][result.item_id];
  },
);

appManager.createSocketListener<SyntheticBondPreviewJobV2, AppState>(
  'synthetic_bond_preview_v2_completed',
  (state, result) => storePreviewV2Job(state, result),
);

appManager.createSocketListener<SyntheticBondPreviewJobV2, AppState>(
  'synthetic_bond_preview_v2_failed',
  (state, result) => storePreviewV2Job(state, result),
);

type CandidatePreviewPayload = { batch_id: string; item_id: string };

export const fetchSyntheticBondCandidatePricePreview = appManager.createApi<
  CandidatePreviewPayload,
  SyntheticBondCandidatePricePreview,
  AppState
>('FETCH_SYNTHETIC_BOND_CANDIDATE_PRICE_PREVIEW', {
  path: '/synthetic_bond_generation/batches/:batch_id/items/:item_id/price-preview',
  method: 'GET',
  startReducer(state, payload) {
    const previous =
      state.synthetic_bonds.candidatePreviewByKey[payload.item_id];
    state.synthetic_bonds.candidatePreviewByKey[payload.item_id] = {
      ...previous,
      fetched: previous?.fetched || false,
      fetching: true,
      error: undefined,
    };
  },
  failReducer(state, error, payload) {
    if (
      syntheticBondPreviewId(state.synthetic_bonds.preview) !== payload.batch_id
    ) {
      return;
    }
    state.synthetic_bonds.candidatePreviewByKey[payload.item_id] = {
      ...state.synthetic_bonds.candidatePreviewByKey[payload.item_id],
      fetched: false,
      fetching: false,
      error: apiErrorText(error, 'Candidate preview failed.'),
    };
    if (apiErrorCode(error) === 'PREVIEW_STALE') {
      state.synthetic_bonds.preview = undefined;
      state.synthetic_bonds.previewError =
        'Source data changed after review. Preview the batch again.';
    }
  },
  successReducer(state, result, payload) {
    if (
      syntheticBondPreviewId(state.synthetic_bonds.preview) !== payload.batch_id
    ) {
      return;
    }
    state.synthetic_bonds.candidatePreviewByKey[payload.item_id] = {
      data: result,
      fetched: true,
      fetching: false,
      error: undefined,
    };
  },
});

type CreatePayload = { batch_id: string; preview_checksum: string };

export const createSyntheticBondBatch = appManager.createApi<
  CreatePayload,
  SyntheticBondBatchStatus,
  AppState
>('CREATE_SYNTHETIC_BOND_BATCH', {
  path: '/synthetic_bond_generation/create',
  method: 'POST',
  startReducer(state) {
    state.synthetic_bonds.createSubmitting = true;
    state.synthetic_bonds.createError = undefined;
  },
  failReducer(state, error) {
    state.synthetic_bonds.createSubmitting = false;
    state.synthetic_bonds.createError = apiErrorText(
      error,
      'Creation could not be queued.',
    );
    if (apiErrorCode(error) === 'PREVIEW_STALE') {
      state.synthetic_bonds.preview = undefined;
      state.synthetic_bonds.candidatePreviewByKey = {};
      state.synthetic_bonds.previewError =
        'Source data changed after review. Preview the batch again.';
    }
  },
  successReducer(state, result, payload) {
    const response =
      result && typeof result === 'object' && 'batch' in result
        ? (result as { batch: SyntheticBondBatchStatus }).batch || result
        : result;
    const batchId =
      response.batch_id || response.id || response.uuid || payload.batch_id;
    state.synthetic_bonds.createSubmitting = false;
    state.synthetic_bonds.createError = undefined;
    state.synthetic_bonds.activeBatchId = batchId;
    state.synthetic_bonds.activeBatch = response;
    state.synthetic_bonds.statusFetching = false;
    state.synthetic_bonds.statusError = undefined;
    state.synthetic_bonds.statusRequestId = undefined;
    state.synthetic_bonds.retrying = false;
    state.synthetic_bonds.retryError = undefined;
    state.synthetic_bonds.retryRequestId = undefined;
  },
});

export type SyntheticBondBatchRequest = {
  batch_id: string;
  _client_request_id: string;
};

export const fetchSyntheticBondBatchStatus = appManager.createApi<
  SyntheticBondBatchRequest,
  SyntheticBondBatchStatus,
  AppState
>('FETCH_SYNTHETIC_BOND_BATCH_STATUS', {
  path: '/synthetic_bond_generation/batches/:batch_id',
  method: 'GET',
  startReducer(state, payload) {
    if (state.synthetic_bonds.activeBatchId !== payload.batch_id) return;
    state.synthetic_bonds.statusFetching = true;
    state.synthetic_bonds.statusError = undefined;
    state.synthetic_bonds.statusRequestId = payload._client_request_id;
  },
  failReducer(state, error, payload) {
    if (
      state.synthetic_bonds.activeBatchId !== payload.batch_id ||
      state.synthetic_bonds.statusRequestId !== payload._client_request_id
    ) {
      return;
    }
    state.synthetic_bonds.statusFetching = false;
    state.synthetic_bonds.statusError = undefined;
    state.synthetic_bonds.statusRequestId = undefined;
    state.synthetic_bonds.statusError = apiErrorText(
      error,
      'Status could not be refreshed.',
    );
  },
  successReducer(state, result, payload) {
    if (
      state.synthetic_bonds.activeBatchId !== payload.batch_id ||
      state.synthetic_bonds.statusRequestId !== payload._client_request_id
    ) {
      return;
    }
    const response =
      result && typeof result === 'object' && 'batch' in result
        ? (result as { batch: SyntheticBondBatchStatus }).batch || result
        : result;
    state.synthetic_bonds.activeBatch = response;
    state.synthetic_bonds.statusFetching = false;
    state.synthetic_bonds.statusError = undefined;
    state.synthetic_bonds.statusRequestId = undefined;
    if ((response.status || '').toUpperCase() === 'STALE') {
      state.synthetic_bonds.preview = undefined;
      state.synthetic_bonds.previewError =
        'Source data changed after review. Preview the batch again.';
    }
  },
});

export const retrySyntheticBondBatch = appManager.createApi<
  SyntheticBondBatchRequest,
  SyntheticBondBatchStatus,
  AppState
>('RETRY_SYNTHETIC_BOND_BATCH', {
  path: '/synthetic_bond_generation/batches/:batch_id/retry',
  method: 'POST',
  startReducer(state, payload) {
    if (state.synthetic_bonds.activeBatchId !== payload.batch_id) return;
    state.synthetic_bonds.statusFetching = false;
    state.synthetic_bonds.statusRequestId = undefined;
    state.synthetic_bonds.retrying = true;
    state.synthetic_bonds.retryError = undefined;
    state.synthetic_bonds.retryRequestId = payload._client_request_id;
  },
  failReducer(state, error, payload) {
    if (
      state.synthetic_bonds.activeBatchId !== payload.batch_id ||
      state.synthetic_bonds.retryRequestId !== payload._client_request_id
    ) {
      return;
    }
    state.synthetic_bonds.retrying = false;
    state.synthetic_bonds.retryRequestId = undefined;
    state.synthetic_bonds.retryError = apiErrorText(
      error,
      'Retry could not be queued.',
    );
    if (apiErrorCode(error) === 'PREVIEW_STALE') {
      state.synthetic_bonds.preview = undefined;
      state.synthetic_bonds.candidatePreviewByKey = {};
      state.synthetic_bonds.previewError =
        'Source data changed after review. Preview the batch again.';
    }
  },
  successReducer(state, result, payload) {
    if (
      state.synthetic_bonds.activeBatchId !== payload.batch_id ||
      state.synthetic_bonds.retryRequestId !== payload._client_request_id
    ) {
      return;
    }
    const response =
      result && typeof result === 'object' && 'batch' in result
        ? (result as { batch: SyntheticBondBatchStatus }).batch || result
        : result;
    state.synthetic_bonds.retrying = false;
    state.synthetic_bonds.retryError = undefined;
    state.synthetic_bonds.retryRequestId = undefined;
    state.synthetic_bonds.activeBatch = response;
  },
});
